import { alpha, Box, Stack, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import {
    CategoryScale,
    Chart as ChartJS,
    type ChartData,
    type ChartOptions,
    Filler,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
} from 'chart.js';
import { format, parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';

import {
    getCumulativeRevenueSeries,
    getRevenueMonthQualifier,
} from 'helpers/serviceProviderBilling';
import useTranslate from 'hooks/useTranslate';
import type {
    AssessmentRevenuePoint,
    BillingRevenuePoint,
    RevenueChartGranularity,
    RevenueChartMode,
    RevenueSeriesKey,
} from 'types';
import { formatCompactCurrency, formatCurrency } from 'utils/formatters';

ChartJS.register(CategoryScale, Filler, LinearScale, LineElement, PointElement, Tooltip);

const SERIES_KEYS: RevenueSeriesKey[] = ['services', 'assessments', 'total'];

interface RevenueChartProps {
    servicePoints: BillingRevenuePoint[];
    assessmentPoints: AssessmentRevenuePoint[];
    currency: string;
    granularity: RevenueChartGranularity;
    mode: RevenueChartMode;
    onModeChange: (mode: RevenueChartMode) => void;
    rangeFrom: string;
    rangeTo: string;
}

const RevenueChart = ({
    servicePoints,
    assessmentPoints,
    currency,
    granularity,
    mode,
    onModeChange,
    rangeFrom,
    rangeTo,
}: RevenueChartProps) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const axisColor = theme.palette.text.secondary;
    const [visibleSeries, setVisibleSeries] = useState<RevenueSeriesKey[]>(SERIES_KEYS);

    const cumulative = useMemo(
        () => getCumulativeRevenueSeries(servicePoints, assessmentPoints, currency),
        [assessmentPoints, currency, servicePoints]
    );
    const formatPointLabel = useCallback(
        (day: string, pattern: string) => {
            const label = format(parseISO(day), pattern);
            if (granularity !== 'month') {
                return label;
            }
            const qualifier = getRevenueMonthQualifier(day, rangeFrom, rangeTo);
            return qualifier
                ? `${label} (${translate(`serviceProviderBilling.revenue.${qualifier}`)})`
                : label;
        },
        [granularity, rangeFrom, rangeTo, translate]
    );
    const seriesConfig = useMemo(
        () => ({
            services: {
                label: translate('serviceProviderBilling.revenue.serviceRevenue'),
                color: theme.palette.primary.main,
                opacity: 0.28,
            },
            assessments: {
                label: translate('serviceProviderBilling.revenue.assessmentRevenueAllCountries'),
                color: theme.palette.success.main,
                opacity: 0.28,
            },
            total: {
                label: translate('serviceProviderBilling.revenue.totalRevenue'),
                color: theme.palette.secondary.main,
                opacity: 0.12,
            },
        }),
        [theme, translate]
    );

    const data = useMemo<ChartData<'line', number[], string>>(
        () => ({
            labels: cumulative.total.map((point) =>
                formatPointLabel(point.day, granularity === 'month' ? 'MMM yyyy' : 'MMM d')
            ),
            datasets: visibleSeries.map((seriesKey) => {
                const { color, label, opacity } = seriesConfig[seriesKey];

                return {
                    label,
                    data: cumulative[seriesKey].map((point) =>
                        mode === 'cumulative' ? point.total : point.revenue
                    ),
                    borderColor: color,
                    borderWidth: seriesKey === 'total' ? 3 : 2,
                    fill: 'origin',
                    backgroundColor: ({ chart }) => {
                        const { ctx, chartArea } = chart;
                        if (!chartArea) {
                            return alpha(color, opacity);
                        }
                        const gradient = ctx.createLinearGradient(
                            0,
                            chartArea.top,
                            0,
                            chartArea.bottom
                        );
                        gradient.addColorStop(0, alpha(color, opacity));
                        gradient.addColorStop(1, alpha(color, 0.02));
                        return gradient;
                    },
                    tension: 0.3,
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    pointHoverBackgroundColor: color,
                };
            }),
        }),
        [cumulative, formatPointLabel, granularity, mode, seriesConfig, visibleSeries]
    );

    const options = useMemo<ChartOptions<'line'>>(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    displayColors: false,
                    callbacks: {
                        title: (items) => {
                            const point = cumulative.total[items[0]?.dataIndex ?? -1];
                            return point
                                ? formatPointLabel(
                                      point.day,
                                      granularity === 'month' ? 'MMMM yyyy' : 'EEE, MMM d'
                                  )
                                : '';
                        },
                        label: (context) => {
                            const seriesKey = visibleSeries[context.datasetIndex];
                            if (!seriesKey) {
                                return '';
                            }
                            const point = cumulative[seriesKey]?.[context.dataIndex];
                            if (!point) {
                                return '';
                            }
                            const label = seriesConfig[seriesKey].label;
                            if (mode === 'monthly') {
                                return `${label}: ${formatCurrency(point.revenue, currency)}`;
                            }
                            return [
                                `${label}: ${formatCurrency(context.parsed.y ?? 0, currency)}`,
                                `${translate(
                                    granularity === 'month'
                                        ? 'serviceProviderBilling.revenue.thatMonth'
                                        : 'serviceProviderBilling.revenue.thatDay'
                                )}: ${formatCurrency(point.revenue, currency)}`,
                            ];
                        },
                    },
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { color: axisColor, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
                },
                y: {
                    beginAtZero: true,
                    border: { display: false },
                    grid: { color: 'rgba(148, 163, 184, 0.2)' },
                    ticks: {
                        color: axisColor,
                        callback: (value) => formatCompactCurrency(Number(value), currency),
                    },
                },
            },
        }),
        [
            axisColor,
            cumulative,
            currency,
            formatPointLabel,
            granularity,
            mode,
            seriesConfig,
            translate,
            visibleSeries,
        ]
    );

    return (
        <Stack spacing={2}>
            {granularity === 'month' ? (
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={mode}
                    onChange={(_event, nextMode: RevenueChartMode | null) => {
                        if (nextMode) {
                            onModeChange(nextMode);
                        }
                    }}
                    aria-label={translate('serviceProviderBilling.revenue.chartMode')}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    <ToggleButton value="monthly" sx={{ textTransform: 'none' }}>
                        {translate('serviceProviderBilling.revenue.monthly')}
                    </ToggleButton>
                    <ToggleButton value="cumulative" sx={{ textTransform: 'none' }}>
                        {translate('serviceProviderBilling.revenue.cumulative')}
                    </ToggleButton>
                </ToggleButtonGroup>
            ) : null}
            <ToggleButtonGroup
                size="small"
                value={visibleSeries}
                onChange={(_event, nextSeries: RevenueSeriesKey[]) => {
                    if (nextSeries.length) {
                        setVisibleSeries(
                            SERIES_KEYS.filter((seriesKey) => nextSeries.includes(seriesKey))
                        );
                    }
                }}
                aria-label={translate('serviceProviderBilling.revenue.series')}
                sx={{ alignSelf: 'flex-start' }}
            >
                {SERIES_KEYS.map((seriesKey) => {
                    const { color, label } = seriesConfig[seriesKey];
                    return (
                        <ToggleButton
                            key={seriesKey}
                            value={seriesKey}
                            aria-label={label}
                            sx={{ gap: 0.75, px: 1.25, textTransform: 'none' }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: color,
                                }}
                            />
                            {label}
                        </ToggleButton>
                    );
                })}
            </ToggleButtonGroup>
            <Box sx={{ height: 320 }}>
                <Line data={data} options={options} />
            </Box>
        </Stack>
    );
};

export default RevenueChart;
